import { toast } from "sonner";

import { ICompasAIChatAction } from "@/types/compass.types";
import { useEditActionPrompt, useInvalidateCompassChat } from "@/hooks/useCompassChat";
import { useRunCompassActions } from "@/hooks/useCompassActions";
import { useCancelQueuedAction } from "@/hooks/useCompassQueue";
import { useDocument } from "@/hooks/useDocument";

type HookDoc = ReturnType<typeof useDocument>["data"];

type ToggleEditingParams = {
  action: ICompasAIChatAction;
  editedPrompt: string;
  doc: HookDoc;
  setIsEditing: (v: boolean) => void;
  editActionMutation: ReturnType<typeof useEditActionPrompt>;
  invalidateCompassChat: ReturnType<typeof useInvalidateCompassChat>;
};

export function runToggleEditing(params: ToggleEditingParams) {
  const { action, editedPrompt, doc, setIsEditing, editActionMutation, invalidateCompassChat } =
    params;
  if (!editedPrompt) {
    toast.error("Prompt cannot be empty", { duration: 2000 });
    return;
  }
  if (editedPrompt === action.prompt || doc?._id == null) {
    setIsEditing(false);
    return;
  }
  const contextId = doc._id;
  editActionMutation.mutate(
    { actionId: action.id, contextId, prompt: editedPrompt },
    {
      onSuccess: () => {
        setIsEditing(false);
        invalidateCompassChat(contextId);
        toast.success("Action updated", { duration: 2000 });
      },
      onError: (error) => {
        console.error("Failed to update action:", error);
        toast.error("Failed to update action", { duration: 2000 });
      },
    }
  );
}

type CancelActionParams = {
  action: ICompasAIChatAction;
  doc: HookDoc;
  cancelActionMutation: ReturnType<typeof useCancelQueuedAction>;
  invalidateCompassChat: ReturnType<typeof useInvalidateCompassChat>;
};

export function runCancelAction(params: CancelActionParams) {
  const { action, doc, cancelActionMutation, invalidateCompassChat } = params;
  if (doc?._id == null) return;
  if (action.queue_item_id == null) {
    toast.info("Action is still being queued — try again in a moment", { duration: 2000 });
    return;
  }
  const documentId = doc._id;
  cancelActionMutation.mutate(
    { documentId, jobIds: [action.queue_item_id] },
    {
      onSuccess: () => {
        invalidateCompassChat(documentId);
        toast.success("Action removed from queue", { duration: 2000 });
      },
      onError: (error) => {
        console.error("Failed to cancel action:", error);
        toast.error("Failed to cancel action", { duration: 2000 });
      },
    }
  );
}

type ExecuteActionParams = {
  action: ICompasAIChatAction;
  editedPrompt: string;
  doc: HookDoc;
  runActionsMutation: ReturnType<typeof useRunCompassActions>;
  invalidateCompassChat: ReturnType<typeof useInvalidateCompassChat>;
};

export function runExecuteAction(params: ExecuteActionParams) {
  const { action, editedPrompt, doc, runActionsMutation, invalidateCompassChat } = params;
  if (doc?._id == null || doc.title.length === 0) return;
  const documentId = doc._id;
  const documentTitle = doc.title;
  const actionRequest = {
    id: action.id,
    prompt: editedPrompt,
    module: action.module || "",
    feature: action.feature || "",
    current_document_id: documentId,
    document_ids: [documentId],
    document_title: [documentTitle],
  };
  runActionsMutation.mutate(
    { requests: [actionRequest], documentId },
    {
      onSuccess: () => {
        toast.success("Compass started processing action", { duration: 2000 });
        invalidateCompassChat(documentId);
      },
      onError: (error) => {
        console.error("Failed to execute action:", error);
        toast.error("Failed to execute action", { duration: 2000 });
      },
    }
  );
}
